"use client";

import React from "react";
import { Cpu, PhoneCall, FileCheck, Wrench, Rocket, ArrowRight, Activity, Clock, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [];
const protocolFeatures = [{
  title: "24-Hour Reports",
  description: "Receive your detailed digital report within one day of the inspection completion.",
  icon: Clock
}, {
  title: "Expert Precision",
  description: "Licensed inspectors identifying hidden issues before they become expensive problems.",
  icon: Cpu
}, {
  title: "Market Confidence",
  description: "Unbiased evaluations that provide peace of mind for both buyers and sellers.",
  icon: TrendingUp
}];
function StepCard({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div data-section-id="159520" data-index={index} ref={ref} className={`
        ${step.colSpan} 
        transition-all duration-1000 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
        ${index > 0 ? 'mt-8 md:mt-24' : ''}
      `}>
      <Card className="bg-card dark:bg-card backdrop-blur-none border-border shadow-lg overflow-hidden relative group hover:border-primary/50 transition-colors duration-500">
        {}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        
        <CardHeader className="pb-4 relative z-10">
          <div className="flex items-center justify-between mb-4">
            <Badge variant="outline" className="font-mono text-primary border-primary/20 bg-primary/5 tracking-widest">
              VERIFIED
            </Badge>
            <step.icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
          </div>
          <div className="space-y-1">
            <p className="font-sans text-xs text-muted-foreground font-medium tracking-widest uppercase">
              {step.label}
            </p>
            <CardTitle className="text-2xl font-medium tracking-tight text-foreground">
              {step.heading}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="relative z-10">
          <CardDescription className="font-sans text-sm leading-relaxed text-muted-foreground font-normal">
            {step.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}
export default function HowItWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: protocolRef,
    isVisible: isProtocolVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="clientreviews" className="bg-background py-[160px] relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-24 transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="secondary" className="mb-6 font-mono tracking-widest uppercase bg-primary/10 text-primary border-primary/20 backdrop-blur-sm">
            Client Feedback
          </Badge>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-6 text-secondary uppercase">
            Verified <br className="hidden md:block" />
            <span className="text-foreground">
              Testimonials
            </span>
          </h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto">
            A reputation built on precision, clarity, and rapid reporting across Miami-Dade and Broward counties.
          </p>

          <div className="mt-12 max-w-xl mx-auto overflow-hidden rounded-xl border border-border bg-card/50 backdrop-blur-sm shadow-sm group">
            <div className="aspect-[21/9] flex flex-col items-center justify-center p-8 bg-muted/5 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-2000 pointer-events-none" />
              <div className="flex items-center gap-1.5 mb-4">
                {[1, 2, 3, 4, 5].map(i => <Activity key={i} className="w-5 h-5 text-primary" />)}
              </div>
              <p className="text-sm font-mono tracking-[0.2em] text-muted-foreground uppercase mb-2">Google Reviews Integration</p>
              <div className="flex items-center gap-3">
                <div className="h-px w-8 bg-border" />
                <span className="text-xs text-muted-foreground font-medium">4.9/5 Average Rating</span>
                <div className="h-px w-8 bg-border" />
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 mb-32">
          {}
          <div className="hidden md:block absolute left-1/2 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-primary/20 to-transparent -translate-x-1/2" />
          
          {steps.map((step, index) => <StepCard key={step.id} step={step} index={index} />)}
        </div>

        {}
        <div ref={protocolRef} className={`transition-all duration-1000 delay-300 ${isProtocolVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <Separator className="bg-border mb-12" />
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 space-y-6">
              <div>
                <h3 className="text-2xl font-bold tracking-tight text-foreground mb-2">
                  The Trusted Standard
                </h3>
                <p className="font-sans text-sm text-muted-foreground font-medium">
                  Engineered exclusively for Trusted Inspections partners.
                </p>
              </div>
              <p className="text-muted-foreground leading-relaxed font-sans">
                Our inspections are performed with a singular focus: protecting your investment. We don't just find issues; we provide the clarity and peace of mind necessary for confident property decisions.
              </p>
              <Button asChild size="lg" className="w-full sm:w-auto group bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/HomePage">
                  Schedule Your Inspection
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
            
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {protocolFeatures.map((feature, index) => <div key={index} data-index={index} className="space-y-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h4 className="font-bold text-foreground">{feature.title}</h4>
                  <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>)}
            </div>
          </div>
          
          <Separator className="bg-border mt-12" />
        </div>

      </div>
    </section>;
}